import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Database, Activity, Users, MessageCircle, Heart, CreditCard, RefreshCw, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useNeo4j } from '../../contexts/Neo4jContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const DatabaseStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected, isLoading, error, healthCheck, reconnect } = useNeo4j();
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const loadDatabaseStats = async () => {
    if (!isConnected) return;
    
    setLoadingStats(true);
    try {
      // Get database statistics
      const query = `
        CALL apoc.meta.stats() YIELD labels, relTypesCount, nodeCount, relCount
        RETURN labels, relTypesCount, nodeCount, relCount
      `;
      
      // Since we don't have apoc, let's use basic queries
      const basicStatsQuery = `
        MATCH (n)
        RETURN 
          count(n) as totalNodes,
          count(DISTINCT labels(n)) as totalLabels
      `;
      
      // For now, we'll show basic connection status
      setStats({
        totalNodes: 0,
        totalLabels: 0,
        totalRelationships: 0,
        users: 0,
        messages: 0,
        matches: 0,
        transactions: 0
      });
    } catch (error) {
      console.error('Failed to load database stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      loadDatabaseStats();
    }
  }, [isConnected]);

  const getStatusIcon = () => {
    if (isLoading) return <RefreshCw className="animate-spin text-blue-500\" size={24} />;
    if (isConnected) return <CheckCircle className="text-green-500" size={24} />;
    if (error) return <XCircle className="text-red-500" size={24} />;
    return <AlertTriangle className="text-yellow-500" size={24} />;
  };

  const getStatusText = () => {
    if (isLoading) return 'Connecting...';
    if (isConnected) return 'Connected';
    if (error) return 'Disconnected';
    return 'Unknown';
  };

  const getStatusColor = () => {
    if (isLoading) return 'text-blue-600 dark:text-blue-400';
    if (isConnected) return 'text-green-600 dark:text-green-400';
    if (error) return 'text-red-600 dark:text-red-400';
    return 'text-yellow-600 dark:text-yellow-400';
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Database Status</h1>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Connection Status */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Database size={32} className="text-purple-600 dark:text-purple-400" />
              <div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Neo4j Database</h2>
                <p className="text-gray-600 dark:text-gray-300">Graph database for Pi Dating</p>
              </div>
            </div>
            {getStatusIcon()}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">Status</h3>
              <p className={`text-lg font-bold ${getStatusColor()}`}>{getStatusText()}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">URI</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">bolt://localhost:7687</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={reconnect}
              disabled={isLoading}
              variant="primary"
              size="sm"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              {isLoading ? 'Connecting...' : 'Reconnect'}
            </Button>
            <Button
              onClick={async () => {
                const healthy = await healthCheck();
                alert(healthy ? 'Database is healthy!' : 'Database health check failed!');
              }}
              disabled={!isConnected}
              variant="outline"
              size="sm"
            >
              <Activity size={16} />
              Health Check
            </Button>
          </div>
        </Card>

        {/* Database Statistics */}
        {isConnected && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Database Statistics</h3>
              <Button
                onClick={loadDatabaseStats}
                disabled={loadingStats}
                variant="outline"
                size="sm"
              >
                <RefreshCw size={16} className={loadingStats ? 'animate-spin' : ''} />
                Refresh
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <Users size={24} className="text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                  {stats?.users || 0}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400">Users</p>
              </div>

              <div className="text-center p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
                <MessageCircle size={24} className="text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-800 dark:text-green-200">
                  {stats?.messages || 0}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">Messages</p>
              </div>

              <div className="text-center p-4 bg-pink-50 dark:bg-pink-900/30 rounded-lg">
                <Heart size={24} className="text-pink-600 dark:text-pink-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-pink-800 dark:text-pink-200">
                  {stats?.matches || 0}
                </p>
                <p className="text-sm text-pink-600 dark:text-pink-400">Matches</p>
              </div>

              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <CreditCard size={24} className="text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                  {stats?.transactions || 0}
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-400">Transactions</p>
              </div>
            </div>
          </Card>
        )}

        {/* Schema Information */}
        {isConnected && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Database Schema</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Node Types</h4>
                <div className="flex flex-wrap gap-2">
                  {['User', 'Message', 'Conversation', 'Transaction'].map((nodeType) => (
                    <span
                      key={nodeType}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                    >
                      {nodeType}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Relationship Types</h4>
                <div className="flex flex-wrap gap-2">
                  {['LIKES', 'MATCHED', 'BLOCKS', 'PARTICIPATES_IN', 'SENT_TO', 'MADE_TRANSACTION'].map((relType) => (
                    <span
                      key={relType}
                      className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm"
                    >
                      {relType}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Configuration */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Configuration</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">Database URI</span>
              <span className="text-sm font-mono text-gray-800 dark:text-gray-200">bolt://localhost:7687</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">Database Name</span>
              <span className="text-sm font-mono text-gray-800 dark:text-gray-200">neo4j</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">Connection Pool Size</span>
              <span className="text-sm font-mono text-gray-800 dark:text-gray-200">50</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">Encryption</span>
              <span className="text-sm font-mono text-gray-800 dark:text-gray-200">Disabled (Development)</span>
            </div>
          </div>
        </Card>

        {/* Setup Instructions */}
        <Card className="p-6 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700">
          <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">
            🚀 Neo4j Setup Instructions
          </h3>
          
          <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
            <p><strong>1. Install Neo4j:</strong></p>
            <p className="ml-4">• Download from <a href="https://neo4j.com/download/" className="underline" target="_blank" rel="noopener noreferrer">neo4j.com/download</a></p>
            <p className="ml-4">• Or use Docker: <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">docker run -p 7474:7474 -p 7687:7687 neo4j</code></p>
            
            <p className="mt-3"><strong>2. Configure Neo4j:</strong></p>
            <p className="ml-4">• Set password: <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">password</code></p>
            <p className="ml-4">• Enable APOC plugin for advanced features</p>
            
            <p className="mt-3"><strong>3. Start Neo4j:</strong></p>
            <p className="ml-4">• Access browser at <a href="http://localhost:7474" className="underline" target="_blank" rel="noopener noreferrer">localhost:7474</a></p>
            <p className="ml-4">• Bolt port: 7687</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DatabaseStatusPage;